import { useCallback, useEffect, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { TID } from "@/lib/testIds";

/**
 * FullscreenToggle
 * -----------------------------------------------------------------------------
 * Small icon button pinned top-right that toggles browser fullscreen
 * (document.documentElement). Keyboard shortcut: `F`.
 *
 * Placed above the HudOverlay (which is pointer-events-none) so the click
 * lands on the button.
 */
export default function FullscreenToggle() {
    const [isFs, setIsFs] = useState(
        typeof document !== "undefined" && !!document.fullscreenElement
    );

    const enter = useCallback(async () => {
        try {
            await document.documentElement.requestFullscreen({ navigationUI: "hide" });
        } catch (err) {
            console.warn("[Fullscreen] enter failed:", err?.message || err);
        }
    }, []);

    const exit = useCallback(async () => {
        if (!document.fullscreenElement) return;
        try {
            await document.exitFullscreen();
        } catch (err) {
            console.warn("[Fullscreen] exit failed:", err?.message || err);
        }
    }, []);

    const toggle = useCallback(() => {
        if (document.fullscreenElement) exit();
        else enter();
    }, [enter, exit]);

    // Reflect real state (ESC / dev-tools can flip us out of FS).
    useEffect(() => {
        const onChange = () => setIsFs(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", onChange);
        return () => document.removeEventListener("fullscreenchange", onChange);
    }, []);

    // `F` shortcut — skip while a text input, textarea, or contenteditable is focused.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== "f" && e.key !== "F") return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const el = document.activeElement;
            const tag = el?.tagName;
            const editable =
                tag === "INPUT" ||
                tag === "TEXTAREA" ||
                tag === "SELECT" ||
                el?.isContentEditable;
            if (editable) return;
            e.preventDefault();
            toggle();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [toggle]);

    const Icon = isFs ? Minimize2 : Maximize2;
    const label = isFs ? "EXIT FS" : "FS";

    return (
        <button
            onClick={toggle}
            className="hidden md:flex fixed top-3 right-14 z-[70] group items-center h-8 border border-virtus-line bg-virtus-surface/95 backdrop-blur hover:border-virtus-orange overflow-hidden transition-[max-width,border-color] duration-200 max-w-[36px] hover:max-w-[140px] pl-[10px] pr-3"
            title={isFs ? "Exit fullscreen (F)" : "Enter fullscreen (F)"}
            aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
            data-testid={TID.fullscreenToggleBtn}
        >
            <Icon
                size={14}
                className="flex-shrink-0 text-virtus-dim group-hover:text-virtus-orange transition-colors"
            />
            <div className="ml-0 group-hover:ml-2 whitespace-nowrap overflow-hidden transition-[margin,opacity] duration-200 opacity-0 group-hover:opacity-100">
                <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-orange leading-none">
                    {label}
                </div>
                <div className="font-mono text-[8px] tracking-[0.22em] uppercase text-virtus-faint leading-none mt-0.5">
                    PRESS F
                </div>
            </div>
        </button>
    );
}
